/**
 * ScenarioCompare — P12 side-by-side A vs B simulation.
 * Renders dual cards + per-metric winner highlight + dual-line chart.
 */
import { useState } from "react";
import { Award, Copy, Play } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from "recharts";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { inr } from "../../lib/format";

export default function ScenarioCompare({ baseRequest }) {
  const [scenarioA, setScenarioA] = useState(null);
  const [scenarioB, setScenarioB] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const copyBaseToA = () => {
    if (!baseRequest) {
      toast.error("Run a baseline simulation first");
      return;
    }
    setScenarioA(JSON.parse(JSON.stringify(baseRequest)));
    toast.success("Baseline copied to Scenario A");
  };
  const copyAToB = () => {
    if (!scenarioA) return;
    setScenarioB(JSON.parse(JSON.stringify(scenarioA)));
    toast.success("Scenario A copied to B — now tweak one variable to compare");
  };

  const run = async () => {
    if (!scenarioA || !scenarioB) {
      toast.error("Set up both scenarios first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/snowball/compare", {
        scenario_a: scenarioA,
        scenario_b: scenarioB,
      });
      setRes(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Compare failed");
    } finally {
      setBusy(false);
    }
  };

  // Tweak the most impactful single variable in scenario B
  // (defined outside component below)

  const dualChart = res ? (() => {
    const N = Math.min(res.scenario_a.monthly.length, res.scenario_b.monthly.length);
    const step = Math.max(1, Math.floor(N / 80));
    return Array.from({ length: N }).filter((_, i) => i % step === 0).map((_, i) => ({
      month: res.scenario_a.monthly[i * step].month,
      A: res.scenario_a.monthly[i * step].fund,
      B: res.scenario_b.monthly[i * step].fund,
    }));
  })() : [];

  const winnerBadge = (winner) =>
    winner === "a" ? <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">A wins</span> :
    winner === "b" ? <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">B wins</span> :
    <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">Tie</span>;

  return (
    <div className="card-flat p-5 space-y-4" data-testid="snowball-compare">
      <div className="flex items-center gap-2">
        <Award size={18} className="text-[#0B7B6E]" />
        <h4 className="font-serif text-lg">Compare scenarios A vs B</h4>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Scenario A */}
        <div className="rounded-md border border-[#e5e1d6] bg-white p-4">
          <div className="flex items-center justify-between mb-2">
            <h5 className="font-medium">Scenario A</h5>
            {!scenarioA ? (
              <Button size="sm" variant="outline" onClick={copyBaseToA} data-testid="compare-load-a">
                <Copy size={12} className="mr-1" /> Load baseline
              </Button>
            ) : (
              <span className="text-[10px] text-emerald-700">Loaded</span>
            )}
          </div>
          <QuickTweak scenario={scenarioA} setScenario={setScenarioA} label="a" />
        </div>

        {/* Scenario B */}
        <div className="rounded-md border border-[#e5e1d6] bg-white p-4">
          <div className="flex items-center justify-between mb-2">
            <h5 className="font-medium">Scenario B</h5>
            {!scenarioB ? (
              <Button size="sm" variant="outline" onClick={copyAToB} disabled={!scenarioA} data-testid="compare-load-b">
                <Copy size={12} className="mr-1" /> Copy A → B
              </Button>
            ) : (
              <span className="text-[10px] text-emerald-700">Loaded — tweak one variable</span>
            )}
          </div>
          <QuickTweak scenario={scenarioB} setScenario={setScenarioB} label="b" />
        </div>
      </div>

      <Button onClick={run} disabled={busy || !scenarioA || !scenarioB} data-testid="compare-run">
        <Play size={14} className="mr-1" /> {busy ? "Running both…" : "Compare scenarios"}
      </Button>

      {res && (
        <div className="space-y-3" data-testid="compare-results">
          <div className={`rounded-md border p-3 ${
            res.overall_winner === "a" ? "bg-emerald-50 border-emerald-200" :
            res.overall_winner === "b" ? "bg-emerald-50 border-emerald-200" :
            "bg-slate-50 border-slate-200"
          }`}>
            <div className="font-medium">
              {res.overall_winner === "a" ? "🏆 Scenario A wins on more metrics" :
               res.overall_winner === "b" ? "🏆 Scenario B wins on more metrics" :
               "Both scenarios are nearly tied"}
            </div>
          </div>

          <div className="space-y-2 text-sm">
            <MetricRow label="Year-20 net wealth" a={inr(res.scenario_a.year20_net_wealth)} b={inr(res.scenario_b.year20_net_wealth)} winner={res.winners.year20_net_wealth} />
            <MetricRow
              label="Sweet spot (earlier is better)"
              a={res.scenario_a.sweet_spot_month != null ? `M${res.scenario_a.sweet_spot_month}` : "Never"}
              b={res.scenario_b.sweet_spot_month != null ? `M${res.scenario_b.sweet_spot_month}` : "Never"}
              winner={res.winners.sweet_spot_month}
            />
            <MetricRow
              label="Velocity score"
              a={res.scenario_a.velocity_score.score}
              b={res.scenario_b.velocity_score.score}
              winner={res.winners.velocity_score}
            />
            <MetricRow
              label="Terminal equity"
              a={inr(res.scenario_a.total_terminal_equity)}
              b={inr(res.scenario_b.total_terminal_equity)}
              winner={res.winners.total_terminal_equity}
            />
            <MetricRow
              label="Self-invested (lower is leaner)"
              a={inr(res.scenario_a.totals.total_self_invested)}
              b={inr(res.scenario_b.totals.total_self_invested)}
              winner={res.winners.total_self_invested}
            />
          </div>

          <div>
            <h5 className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Snowball Fund over time</h5>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={dualChart}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="month" tickFormatter={(m) => `M${m}`} />
                <YAxis tickFormatter={(v) => v >= 10000000 ? `₹${(v / 10000000).toFixed(1)}cr` : `₹${(v / 100000).toFixed(0)}L`} />
                <Tooltip formatter={(v) => inr(v)} labelFormatter={(m) => `Month ${m}`} />
                <Legend />
                <Line type="monotone" dataKey="A" stroke="#0B7B6E" dot={false} />
                <Line type="monotone" dataKey="B" stroke="#C85A32" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricRow({ label, a, b, winner }) {
  return (
    <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center py-1.5 border-b border-[#e5e1d6] last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-mono ${winner === "a" ? "font-semibold text-emerald-700" : ""}`}>{a}</span>
      <span className="text-muted-foreground text-xs">vs</span>
      <span className={`font-mono ${winner === "b" ? "font-semibold text-emerald-700" : ""}`}>{b}</span>
    </div>
  );
}

function QuickTweak({ scenario, setScenario, label }) {
  if (!scenario) return null;
  const setProp0 = (key, value) => setScenario({
    ...scenario,
    properties: scenario.properties.map((p, i) => (i === 0 ? { ...p, [key]: value } : p)),
  });
  return (
    <div className="space-y-2 text-xs">
      <div>
        <div className="text-muted-foreground mb-1">Monthly salary surplus (₹)</div>
        <Input
          type="number"
          value={scenario.monthly_salary_surplus}
          onChange={(e) => setScenario({ ...scenario, monthly_salary_surplus: Number(e.target.value) || 0 })}
          className="font-mono"
          data-testid={`compare-salary-${label}`}
        />
      </div>
      <div>
        <div className="text-muted-foreground mb-1">Starting fund (₹)</div>
        <Input
          type="number"
          value={scenario.starting_snowball_fund}
          onChange={(e) => setScenario({ ...scenario, starting_snowball_fund: Number(e.target.value) || 0 })}
          className="font-mono"
          data-testid={`compare-fund-${label}`}
        />
      </div>
      <div>
        <div className="text-muted-foreground mb-1">Property 1 rent (₹/mo)</div>
        <Input
          type="number"
          value={scenario.properties[0]?.monthly_rent || 0}
          onChange={(e) => setProp0("monthly_rent", Number(e.target.value) || 0)}
          className="font-mono"
          data-testid={`compare-rent-${label}`}
        />
      </div>
      <div>
        <div className="text-muted-foreground mb-1">Property 1 appreciation %</div>
        <Input
          type="number"
          value={scenario.properties[0]?.annual_appreciation_pct || 0}
          step="0.5"
          onChange={(e) => setProp0("annual_appreciation_pct", Number(e.target.value) || 0)}
          className="font-mono"
          data-testid={`compare-apprec-${label}`}
        />
      </div>
    </div>
  );
}
